import {Trans} from '../i18n/trans';
import {Navbar} from '../ui/navigation/navbar/navbar';
import {Form} from '../ui/forms/form';
import {useForm} from 'react-hook-form';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from './use-submit-contact-form';
import {FormTextField} from '../ui/forms/input-field/text-field/text-field';
import {Button} from '../ui/buttons/button';
import {useRecaptcha} from '../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../seo/static-page-title';
import {Footer} from '@common/ui/footer/footer';

export function ContactUsPage() {
  const form = useForm<ContactPagePayload>();
  const submitForm = useSubmitContactForm(form);
  const {verify, isVerifying} = useRecaptcha('contact');

  return (
    <div style={{display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f7f7f5'}}>
      <StaticPageTitle>
        <Trans message="Contact us" />
      </StaticPageTitle>

      <Navbar color="bg" border="border-b" className="lp-sticky-nav flex-shrink-0" menuPosition="homepage-navbar" />

      {/* Hero banner */}
      <div style={{backgroundColor:'#1a0800', backgroundImage:'radial-gradient(120% 120% at 0% 0%, #FFB23E 0%, transparent 50%), radial-gradient(120% 120% at 100% 0%, #FF2E63 0%, transparent 45%), radial-gradient(140% 140% at 50% 120%, #FF4F00 0%, transparent 60%)', padding: '64px 24px 72px', textAlign: 'center', position: 'relative', overflow: 'hidden', zIndex: 0}}>
        <div style={{position: 'relative'}}>
          <span style={{display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginBottom: 14}}>
            Get in touch
          </span>
          <h1 style={{fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1, margin: '0 0 16px'}}>
            We'd love to hear from you
          </h1>
          <p style={{fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 480, margin: '0 auto', lineHeight: 1.7}}>
            Have a question, feedback, or just want to say hello? Send us a message and we'll get back to you within 24 hours.
          </p>
        </div>
      </div>

      {/* Main content */}
      <div className="contact-grid" style={{flex: 1, maxWidth: 1100, margin: '0 auto', width: '100%', padding: '64px 24px 80px', display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 48, alignItems: 'flex-start'}}>

        {/* Left — contact info */}
        <div>
          <h2 style={{fontSize: 22, fontWeight: 700, color: '#0f1523', marginBottom: 8, marginTop: 0}}>Contact Information</h2>
          <p style={{fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 40}}>
            Reach out via the form or contact us directly through any of the channels below.
          </p>

          {[
            {
              label: 'Email',
              value: 'support@linkinbio.site',
              href: 'mailto:support@linkinbio.site',
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="3"/>
                  <path d="m2 7 10 7 10-7"/>
                </svg>
              ),
            },
            {
              label: 'Response time',
              value: 'Within 24 hours',
              href: null,
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
              ),
            },
            {
              label: 'Support hours',
              value: 'Mon – Fri, 9am – 6pm',
              href: null,
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2"/>
                  <path d="M16 2v4M8 2v4M3 10h18"/>
                </svg>
              ),
            },
          ].map(item => (
            <div key={item.label} style={{display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 28}}>
              <div style={{width: 44, height: 44, borderRadius: 12, background: 'rgba(255,79,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff4f00', flexShrink: 0}}>
                {item.icon}
              </div>
              <div>
                <div style={{fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4}}>{item.label}</div>
                {item.href ? (
                  <a href={item.href} style={{fontSize: 15, color: '#0f1523', fontWeight: 500, textDecoration: 'none'}}
                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#ff4f00'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = '#0f1523'; }}>
                    {item.value}
                  </a>
                ) : (
                  <div style={{fontSize: 15, color: '#0f1523', fontWeight: 500}}>{item.value}</div>
                )}
              </div>
            </div>
          ))}

          {/* Divider */}
          <div style={{borderTop: '1px solid #e5e7eb', marginTop: 8, paddingTop: 32}}>
            <div style={{fontSize: 13, color: '#9ca3af', marginBottom: 16}}>Follow us</div>
            <div style={{display: 'flex', gap: 10}}>
              {[
                { label: 'Instagram', href: '#', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/></svg> },
                { label: 'Twitter', href: '#', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                { label: 'TikTok', href: '#', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg> },
                { label: 'LinkedIn', href: '#', icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg> },
              ].map(s => (
                <a key={s.label} href={s.href} aria-label={s.label}
                  style={{width: 36, height: 36, borderRadius: '50%', border: '1.5px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', textDecoration: 'none', transition: 'all 0.2s'}}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = '#ff4f00'; el.style.color = '#ff4f00'; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.borderColor = '#e5e7eb'; el.style.color = '#6b7280'; }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="contact-form-card" style={{background: '#fff', borderRadius: 20, padding: '40px 40px', boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 20px 50px rgba(0,0,0,0.08)'}}>
          <h2 style={{fontSize: 20, fontWeight: 700, color: '#0f1523', marginBottom: 6, marginTop: 0}}>Send a message</h2>
          <p style={{fontSize: 14, color: '#9ca3af', marginBottom: 32}}>Fill in the form below and we'll respond shortly.</p>

          <Form
            form={form}
            onSubmit={async value => {
              const isValid = await verify();
              if (isValid) submitForm.mutate(value);
            }}
          >
            <div className="contact-name-email" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16}}>
              <FormTextField
                label={<Trans message="Name" />}
                name="name"
                required
              />
              <FormTextField
                label={<Trans message="Email" />}
                name="email"
                required
                type="email"
              />
            </div>
            <FormTextField
              label={<Trans message="Message" />}
              name="message"
              required
              inputElementType="textarea"
              className="mb-24"
              rows={6}
            />
            <Button
              type="submit"
              variant="flat"
              color="primary"
              size="md"
              radius="rounded-full"
              disabled={submitForm.isPending || isVerifying}
              style={{width: '100%', padding: '12px', fontSize: 14, fontWeight: 600}}
            >
              <Trans message="Send message" />
            </Button>
          </Form>
        </div>

      </div>

      <style>{`
        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
            padding: 36px 16px 56px !important;
            gap: 32px !important;
          }
          .contact-form-card {
            padding: 28px 20px !important;
            border-radius: 16px !important;
          }
          .contact-name-email {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <Footer />
    </div>
  );
}
